// PostHog analytics for cloak-file-web pre-login surface (/cloak/*).
//
// Boundary: identical event names + property names as the marketing site's
// cloakapps-website/js/analytics.js (the "shared spec, separate code" half of
// the Hybrid pattern in cloakapps-docs/posthog-stripe/CLAUDE.md). Both pages
// live on www.cloakapps.com so PostHog's localStorage distinct_id auto-stitches
// across the cloak.html → /cloak/signup → /cloak/verify-email funnel.
//
// PostHog stops the moment the React app sees an auth token (stopOnLogin) —
// authenticated product usage belongs in internal Cassandra analytics, never
// PostHog.

import posthog from 'posthog-js';

// Replace via build step if you ever rotate the key. Public write-only.
const KEY = 'phc_nQAdtrc6eURDHzeUWwyaxouwHxsQ8X39DVt4VmGcffZW';
const HOST = 'https://us.i.posthog.com';
const ENABLED = KEY.indexOf('PLACEHOLDER') === -1;

const ATTR_COOKIE = 'cloak_attr';
const ATTR_DOMAIN = '.cloakapps.com';
const ATTR_TTL_DAYS = 90;

let initialized = false;
let stopped = false;

function pageCategory(path) {
  if (path.indexOf('/cloak/signup') === 0) return 'cloak_signup';
  if (path.indexOf('/cloak/login') === 0) return 'cloak_login';
  if (path.indexOf('/cloak/verify-email') === 0) return 'cloak_verify';
  if (path.indexOf('/cloak/join-account') === 0) return 'cloak_join';
  if (path.indexOf('/cloak/forgot-password') === 0) return 'cloak_forgot_password';
  if (path.indexOf('/cloak/reset-password') === 0) return 'cloak_reset_password';
  if (path.indexOf('/cloak/enterprise-login') === 0) return 'cloak_enterprise_login';
  if (path.indexOf('/cloak/newsu') === 0) return 'cloak_landing_signup';
  if (path.indexOf('/cloak/newqr') === 0) return 'cloak_landing_qr';
  if (path.indexOf('/cloak/newgwy') === 0) return 'cloak_landing_gwy';
  if (path.indexOf('/cloak/qr') === 0) return 'cloak_qr';
  if (path.indexOf('/cloak/exclude') === 0) return 'cloak_unsubscribe';
  return 'cloak_other';
}

function sourceArea(path) {
  return path.indexOf('/cloak/') === 0 ? 'cloak_prelogin' : 'marketing';
}

function parseUtm() {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const out = {};
  ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','campaign_id']
    .forEach((k) => { const v = p.get(k); if (v) out[k] = v; });
  return out;
}

function readAttrCookie() {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + ATTR_COOKIE + '=([^;]*)'));
    return m ? JSON.parse(decodeURIComponent(m[1])) : null;
  } catch (e) { return null; }
}

function writeAttrCookie(payload) {
  try {
    const v = encodeURIComponent(JSON.stringify(payload));
    document.cookie = ATTR_COOKIE + '=' + v +
      '; Domain=' + ATTR_DOMAIN +
      '; Path=/; Max-Age=' + (60 * 60 * 24 * ATTR_TTL_DAYS) +
      '; Secure; SameSite=Lax';
  } catch (e) {}
}

function persistAttribution() {
  const existing = readAttrCookie() || {};
  const utm = parseUtm();
  const now = new Date().toISOString();
  const next = {
    first_touch_ts: existing.first_touch_ts || now,
    last_touch_ts: now,
    first_referrer: existing.first_referrer || document.referrer || null,
    last_referrer: document.referrer || existing.last_referrer || null,
    first_landing_path: existing.first_landing_path || window.location.pathname,
    utm_source: utm.utm_source || existing.utm_source || null,
    utm_medium: utm.utm_medium || existing.utm_medium || null,
    utm_campaign: utm.utm_campaign || existing.utm_campaign || null,
    utm_term: utm.utm_term || existing.utm_term || null,
    utm_content: utm.utm_content || existing.utm_content || null,
    campaign_id: utm.campaign_id || existing.campaign_id || null,
    distinct_id: existing.distinct_id || null,
    session_id: existing.session_id || null,
  };
  writeAttrCookie(next);
  return next;
}

export function init() {
  if (!ENABLED || initialized || stopped) return;
  if (typeof window === 'undefined') return;
  const attr = persistAttribution();
  const path = window.location.pathname;
  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: 'identified_only',
    // $pageview is required by PostHog's Web Analytics section, which is
    // hardcoded to $pageview/$pageleave and cannot read our custom
    // `page_viewed`. Independent of autocapture: $pageview sends URL and
    // referrer only, never DOM/click/form data, so the pre-login boundary
    // rules are unaffected. This is an SPA, so 'history_change' is required —
    // plain `true` would only fire on hard load, not on route changes.
    // stopOnLogin()'s opt_out_capturing() suppresses these too, so no
    // $pageview fires after the auth boundary.
    capture_pageview: 'history_change',
    autocapture: false,
    disable_session_recording: true,
    loaded: (ph) => {
      const current = readAttrCookie() || attr;
      current.distinct_id = ph.get_distinct_id();
      try {
        if (ph.sessionManager && ph.sessionManager.checkAndGetSessionAndWindowId) {
          current.session_id = ph.sessionManager.checkAndGetSessionAndWindowId(true).sessionId;
        }
      } catch (e) {}
      writeAttrCookie(current);
    },
  });
  posthog.register({
    host: window.location.host,
    path,
    source_area: sourceArea(path),
    page_category: pageCategory(path),
    utm_source: attr.utm_source,
    utm_medium: attr.utm_medium,
    utm_campaign: attr.utm_campaign,
    utm_term: attr.utm_term,
    utm_content: attr.utm_content,
    campaign_id: attr.campaign_id,
    referrer: document.referrer || null,
  });
  initialized = true;
  // Fire the landing pageview now that init is done.
  pageViewed(path, document.title);
}

export function pageViewed(path, title) {
  if (!ENABLED || !initialized || stopped) return;
  // Re-register path-derived base props so subsequent events on this page
  // carry the right source_area / page_category.
  posthog.register({
    path,
    source_area: sourceArea(path),
    page_category: pageCategory(path),
  });
  posthog.capture('page_viewed', { page: title || document.title, url: path });
}

export function trackCTA(label, location, destination) {
  if (!ENABLED || !initialized || stopped) return;
  posthog.capture('cta_clicked', {
    cta_label: label,
    location: location || null,
    destination: destination || null,
  });
}

export function trackSignupStarted(sourcePage, plan) {
  if (!ENABLED || !initialized || stopped) return;
  posthog.capture('signup_started', { source_page: sourcePage, plan: plan || null });
}

export function trackPlanSelected(plan, priceUsd, billingInterval) {
  if (!ENABLED || !initialized || stopped) return;
  posthog.capture('plan_selected', {
    plan,
    price_usd: priceUsd,
    billing_interval: billingInterval || 'month',
  });
}

// Fire ONCE on the verify-email success transition. Authenticated product
// analytics take over from this point — never call PostHog after.
export function trackSignupCompleted(sourcePage, plan) {
  if (!ENABLED || !initialized || stopped) return;
  posthog.capture('signup_completed', { source_page: sourcePage, plan: plan || 'free' });
}

// Call once when state.auth.token first becomes truthy. Idempotent — safe to
// call multiple times; subsequent calls no-op.
export function stopOnLogin() {
  if (!ENABLED || stopped) return;
  stopped = true;
  try {
    posthog.reset();
    posthog.opt_out_capturing();
  } catch (e) {}
}

// Map "encrypt-solo|team|business" plan code → display price for plan_selected.
// Kept tiny and inline so /cloak/ stays a thin pre-login shell.
export function priceForCloakFilesPlan(plan) {
  switch (plan) {
    case 'encrypt-solo': return 9;
    case 'encrypt-team': return 29;
    case 'encrypt-business': return 49;
    default: return null;
  }
}
